const MP4_RECORDING_MIME_TYPES = [
  'video/mp4;codecs=avc1.42E01E,mp4a.40.2',
  'video/mp4;codecs="avc1.42E01E,mp4a.40.2"',
  'video/mp4;codecs=avc1.42E01E',
  'video/mp4;codecs=avc1',
  'video/mp4',
] as const;

export interface VideoRecorderSelection {
  recorder: MediaRecorder;
  requestedMimeType: string;
}

export function createMp4VideoRecorder(
  stream: MediaStream
): VideoRecorderSelection {
  if (typeof MediaRecorder === 'undefined') {
    throw new Error('This browser does not support video recording.');
  }

  for (const mimeType of MP4_RECORDING_MIME_TYPES) {
    if (!MediaRecorder.isTypeSupported(mimeType)) continue;

    try {
      return {
        recorder: new MediaRecorder(stream, { mimeType }),
        requestedMimeType: mimeType,
      };
    } catch (error) {
      console.warn(
        `[VideoRecorder] Browser advertised but could not start ${mimeType}:`,
        error
      );
    }
  }

  throw new Error(
    'This browser cannot record MP4 video. Please use a current version of Chrome, Edge, or Safari.'
  );
}

/** Ensure the recorder really produced MP4 bytes before upload. */
export function assertRecordedMp4(
  recorderMimeType: string,
  requestedMimeType: string,
  chunks: Blob[]
): 'video/mp4' {
  const actualType =
    recorderMimeType ||
    chunks.find((chunk) => !!chunk.type)?.type ||
    requestedMimeType;

  if (!actualType.toLowerCase().startsWith('video/mp4')) {
    throw new Error(
      `The browser produced ${
        actualType || 'an unknown format'
      } instead of MP4. The video was not uploaded.`
    );
  }

  return 'video/mp4';
}

export function getCapturedMediaFileName(
  mimeType: string,
  timestamp = Date.now()
): string {
  return mimeType === 'video/mp4'
    ? `video_${timestamp}.mp4`
    : `photo_${timestamp}.jpg`;
}

export function readVideoBlobAsDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result);
      } else {
        reject(new Error('The recorded video could not be read.'));
      }
    };
    reader.onerror = () =>
      reject(
        reader.error ?? new Error('The recorded video could not be read.')
      );
    reader.readAsDataURL(blob);
  });
}
